import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useNavigate, useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { colors } from './theme';
import LoadingSpinner from './components/LoadingSpinner';
import ProductThumb from './components/ProductThumb';

const API_BASE = process.env.REACT_APP_API_URL;

// --- Organizations list ---
export function AdminOrgsPage() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/admin/organizations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setOrgs(data.organizations || []);
      }
    } catch (err) {
      console.error('Failed to load organizations', err);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { load(); }, [load]);

  const handleVerify = async (orgId, verified) => {
    try {
      const token = await getToken();
      await fetch(`${API_BASE}/admin/organizations/${orgId}/${verified ? 'unverify' : 'verify'}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      load();
    } catch (err) {
      console.error('Verify toggle failed', err);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: colors.bg || '#0b0f0d', padding: '0' }}>
      <div style={{
        background: 'rgba(255,255,255,0.03)',
        borderBottom: '1px solid rgba(255,255,255,0.07)',
        padding: '1rem 1.5rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div>
          <div className="ni-eyebrow">Admin</div>
          <h1 style={{ color: colors.textStrong, margin: 0, fontSize: '1.4rem' }}>Companies</h1>
        </div>
        <button className="ni-button ni-button--ghost" onClick={() => navigate(-1)}>← Back</button>
      </div>

      <div style={{ padding: '1.5rem' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
            <LoadingSpinner />
          </div>
        ) : orgs.length === 0 ? (
          <p style={{ color: colors.textMuted, textAlign: 'center', padding: '3rem 0' }}>
            No companies created yet.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {orgs.map(org => (
              <div key={org._id} style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '10px',
                padding: '1rem 1.25rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '0.75rem'
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <span style={{ color: colors.textStrong, fontWeight: 700, fontSize: '1rem' }}>{org.name}</span>
                    {org.verified && (
                      <span style={{
                        background: 'rgba(70,187,137,0.15)', color: '#46bb89',
                        border: '1px solid rgba(70,187,137,0.3)', borderRadius: '999px',
                        fontSize: '0.7rem', fontWeight: 600, padding: '0.1rem 0.5rem'
                      }}>✓ Verified</span>
                    )}
                  </div>
                  <div style={{ color: colors.textMuted, fontSize: '0.82rem', marginTop: '0.3rem' }}>
                    {org.memberCount} member{org.memberCount !== 1 ? 's' : ''} · {org.deviceCount} device{org.deviceCount !== 1 ? 's' : ''} claimed
                    {org.createdAt && ` · Created ${new Date(org.createdAt).toLocaleDateString()}`}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                  <button
                    className="ni-button ni-button--ghost"
                    style={{ fontSize: '0.8rem', padding: '0.3rem 0.8rem' }}
                    onClick={() => handleVerify(org._id, org.verified)}
                  >
                    {org.verified ? 'Unverify' : 'Verify'}
                  </button>
                  <Link to={`/admin/organizations/${org._id}`}>
                    <button className="ni-button ni-button--primary" style={{ fontSize: '0.8rem', padding: '0.3rem 0.8rem' }}>
                      View →
                    </button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// --- Single org detail ---
export function AdminOrgDetailPage() {
  const { getToken } = useAuth();
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [org, setOrg] = useState(null);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API_BASE}/admin/organizations/${orgId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setOrg(data.org);
          setDevices(data.devices || []);
        }
      } catch (err) {
        console.error('Failed to load org detail', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [orgId, getToken]);

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <LoadingSpinner />
    </div>
  );
  if (!org) return <div style={{ padding: '2rem', color: colors.textMuted }}>Company not found.</div>;

  return (
    <div style={{ minHeight: '100vh', background: colors.bg || '#0b0f0d' }}>
      <div style={{
        background: 'rgba(255,255,255,0.03)',
        borderBottom: '1px solid rgba(255,255,255,0.07)',
        padding: '1rem 1.5rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '0.75rem'
      }}>
        <div>
          <div className="ni-eyebrow">Admin · Companies</div>
          <h1 style={{ color: colors.textStrong, margin: 0, fontSize: '1.4rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            {org.name}
            {org.verified && (
              <span style={{
                background: 'rgba(70,187,137,0.15)', color: '#46bb89',
                border: '1px solid rgba(70,187,137,0.3)', borderRadius: '999px',
                fontSize: '0.75rem', fontWeight: 600, padding: '0.15rem 0.6rem'
              }}>✓ Verified</span>
            )}
          </h1>
        </div>
        <button className="ni-button ni-button--ghost" onClick={() => navigate('/admin/organizations')}>
          ← All companies
        </button>
      </div>

      <div style={{ padding: '1.5rem' }}>
        <h2 style={{ color: colors.textStrong, fontSize: '1rem', marginBottom: '1rem' }}>
          Claimed devices ({devices.length})
        </h2>
        {devices.length === 0 ? (
          <p style={{ color: colors.textMuted }}>No devices claimed by this company.</p>
        ) : (
          <div className="ni-device-grid">
            {devices.map(device => (
              <Link key={device._id} to={`/device/${device._id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                <div className="ni-device-card">
                  <div className="ni-device-card__media">
                    <ProductThumb
                          productNumber={device.productNumber}
                          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        />
                  </div>
                  <div className="ni-device-card__body">
                    <strong className="ni-device-card__title">{device.productNumber}</strong>
                    <p className="ni-device-card__meta">Service Tag: {device.serviceTagNumber ?? 'N/A'}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
